import React, { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ImageIcon, X, CalendarDays } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  FetchAllAnnouncementsAction,
  CreateAnnouncementAction,
  UpdateAnnouncementAction,
  RemoveAnnouncementAction,
} from "../../Store/Actions/AnnouncementActions";
import { AnnouncementActionConst } from "../../Store/Constants/AnnouncementConstants";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const EMPTY_FORM = { title: "", message: "", imageUrl: "", expiryDate: "" };

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function isExpired(expiryDate) {
  if (!expiryDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(expiryDate) < today;
}

function truncate(str, n = 80) {
  if (!str) return "—";
  return str.length > n ? str.slice(0, n) + "…" : str;
}

// ── Image Upload Helper ────────────────────────────────────────────────────────
async function uploadImage(file) {
  const token = localStorage.getItem("accessToken");
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGE}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Upload failed");
  return result.url || result.data?.url;
}

// ── Announcement Form Modal ────────────────────────────────────────────────────
function AnnouncementModal({ mode, initial, onClose, onSubmit, submitting }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [imagePreview, setImagePreview] = useState(initial?.imageUrl || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  async function handleFileChange(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, imageUrl: url }));
      setImagePreview(url);
    } catch {
      toast.error("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  const handleRemoveImage = () => {
    setForm((f) => ({ ...f, imageUrl: "" }));
    setImagePreview("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    if (!form.message.trim()) { toast.error("Message is required."); return; }
    if (!form.expiryDate) { toast.error("Expiry date is required."); return; }
    onSubmit({ ...form, imageUrl: form.imageUrl || undefined });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="font-popinsSemiBold text-base text-gray-800">
            {mode === "create" ? "Create Announcement" : "Edit Announcement"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-popinsSemiBold text-gray-600 mb-1">Title *</label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Announcement title…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-popinsRegular focus:outline-none focus:border-themeColor"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-popinsSemiBold text-gray-600 mb-1">Message *</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Write the announcement message…"
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-popinsRegular focus:outline-none focus:border-themeColor resize-none"
            />
          </div>

          {/* Image */}
          <div>
            <label className="block text-xs font-popinsSemiBold text-gray-600 mb-1">Image (optional)</label>
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="preview" className="w-full h-40 object-cover rounded-lg border border-gray-100" />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full p-1 text-gray-600 transition"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm font-popinsRegular text-gray-500 hover:border-themeColor hover:text-themeColor transition w-full justify-center disabled:opacity-50"
              >
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />}
                {uploading ? "Uploading…" : "Click to upload image"}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block text-xs font-popinsSemiBold text-gray-600 mb-1">Expiry Date *</label>
            <input
              type="date"
              name="expiryDate"
              value={form.expiryDate}
              onChange={handleChange}
              min={new Date().toISOString().split("T")[0]}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-popinsRegular focus:outline-none focus:border-themeColor"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-popinsMedium text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || uploading}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-themeColor text-white text-sm font-popinsMedium hover:bg-opacity-90 transition disabled:opacity-60"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {mode === "create" ? "Create" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete Confirm Modal ───────────────────────────────────────────────────────
function DeleteModal({ title, onClose, onConfirm, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mx-auto mb-4">
            <Trash2 size={20} className="text-red-500" />
          </div>
          <h2 className="text-base font-popinsSemiBold text-gray-800 text-center">Delete Announcement</h2>
          <p className="text-sm font-popinsRegular text-gray-500 text-center mt-2">
            Are you sure you want to delete{" "}
            <span className="font-popinsMedium text-gray-700">"{title}"</span>?
            This action cannot be undone.
          </p>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-popinsMedium text-gray-600 border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-popinsMedium bg-red-500 text-white hover:bg-red-600 transition disabled:opacity-60"
          >
            {deleting && <Loader2 size={14} className="animate-spin" />}
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
const MainAnnouncement = () => {
  const dispatch = useDispatch();
  const { announcements, loader, actionSuccess, actionError } = useSelector((state) => state.announcement);

  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data?: {...} }
  const [deleteTarget, setDeleteTarget] = useState(null); // announcement object
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const processedRef = useRef(null);

  useEffect(() => { dispatch(FetchAllAnnouncementsAction()); }, [dispatch]);

  // Handle action success
  useEffect(() => {
    if (actionSuccess && !processedRef.current) {
      processedRef.current = actionSuccess;
      const msg = modal?.mode === "create"
        ? "Announcement created!"
        : deleteTarget
          ? "Announcement deleted!"
          : "Announcement updated!";
      toast.success(msg);
      setSubmitting(false);
      setDeleting(false);
      setModal(null);
      setDeleteTarget(null);
      dispatch({ type: AnnouncementActionConst.CLEAR_ANNOUNCEMENT_ACTION_STATE });
    }
  }, [actionSuccess, dispatch, modal, deleteTarget]);

  // Handle action error
  useEffect(() => {
    if (actionError) {
      toast.error(actionError.message || "Action failed. Please try again.");
      setSubmitting(false);
      setDeleting(false);
      dispatch({ type: AnnouncementActionConst.CLEAR_ANNOUNCEMENT_ACTION_STATE });
    }
  }, [actionError, dispatch]);

  const handleCreate = (data) => {
    processedRef.current = null;
    setSubmitting(true);
    dispatch(CreateAnnouncementAction(data));
  };

  const handleUpdate = (data) => {
    processedRef.current = null;
    setSubmitting(true);
    dispatch(UpdateAnnouncementAction(modal.data.id, data));
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    processedRef.current = null;
    setDeleting(true);
    dispatch(RemoveAnnouncementAction(deleteTarget.id));
  };

  const openEdit = (a) => {
    setModal({
      mode: "edit",
      data: {
        ...a,
        imageUrl: a.imageUrl || "",
        expiryDate: a.expiryDate || "",
      },
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-popinsSemiBold text-gray-800">Announcements</h1>
          <p className="text-sm font-popinsRegular text-gray-500 mt-1">
            Create and manage announcements shown to NonProfit users on login.
          </p>
        </div>
        <button
          onClick={() => setModal({ mode: "create" })}
          className="flex items-center gap-2 bg-themeColor text-white px-5 py-2.5 rounded-lg text-sm font-popinsMedium hover:bg-opacity-90 transition"
        >
          <Plus size={16} />
          Create Announcement
        </button>
      </div>

      {/* Table */}
      {loader && announcements.length === 0 ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-themeColor" size={32} />
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center shadow-sm">
          <CalendarDays size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="font-popinsMedium text-gray-500">No announcements yet</p>
          <p className="text-sm font-popinsRegular text-gray-400 mt-1">
            Create one to start showing popups to NonProfit users.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide w-[22%]">Title</th>
                  <th className="text-left px-5 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide w-[35%]">Message</th>
                  <th className="text-left px-5 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide w-[8%]">Image</th>
                  <th className="text-left px-5 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide w-[14%]">Expiry Date</th>
                  <th className="text-left px-5 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide w-[11%]">Status</th>
                  <th className="text-right px-5 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide w-[10%]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {announcements.map((a) => {
                  const expired = isExpired(a.expiryDate);
                  return (
                    <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <span className="font-popinsMedium text-gray-800 line-clamp-1">{a.title}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-popinsRegular text-gray-500 text-xs">{truncate(a.message)}</span>
                      </td>
                      <td className="px-5 py-4">
                        {a.imageUrl ? (
                          <img src={a.imageUrl} alt="" className="w-10 h-10 object-cover rounded-md border border-gray-100" />
                        ) : (
                          <span className="text-gray-300 text-xs font-popinsRegular">None</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-popinsRegular text-gray-500">{formatDate(a.expiryDate)}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-popinsSemiBold ${
                          expired ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                        }`}>
                          {expired ? "Expired" : "Active"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(a)}
                            title="Edit"
                            className="text-gray-400 hover:text-themeColor transition-colors"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(a)}
                            title="Delete"
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modal && (
        <AnnouncementModal
          mode={modal.mode}
          initial={modal.data}
          onClose={() => { setModal(null); setSubmitting(false); }}
          onSubmit={modal.mode === "create" ? handleCreate : handleUpdate}
          submitting={submitting}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <DeleteModal
          title={deleteTarget.title}
          onClose={() => { setDeleteTarget(null); setDeleting(false); }}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </div>
  );
};

export default MainAnnouncement;
